'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { BrandCardItem, BrandFamilyLocale, getBrandDisplayName } from '../../types/brandFamily';
import fullLogoBlack from '../../assets/images/full-logo-black.svg';
import { useTranslations, useLocale } from 'next-intl';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import styles from './BrandCardGrid.module.css';



interface BrandCardGridProps {
  brands: BrandCardItem[];
}

export const BrandCardGrid: React.FC<BrandCardGridProps> = ({ brands }) => {
  const t = useTranslations('brands');
  const locale = useLocale() as BrandFamilyLocale;
  const { localizeFullPath } = useLocalizedPath();

  if (!brands || brands.length === 0) {
    return (
      <div className={styles.noResults}>
        <h3 className={styles.noResultsTitle}>{t('noResults.title')}</h3>
        <p className={styles.noResultsDesc}>{t('noResults.description')}</p>
      </div>
    );
  }

  return (
    <div className={styles.brandGrid}>
      {brands.map((brand, index) => (
        <Link
          key={brand._id || brand.slug}
          href={localizeFullPath(`/brands/${brand.slug}`)}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <div
            className={styles.brandCard}
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <div className={styles.brandBackgroundGradient} />
            <div className={styles.cardImage}>
              <div className={styles.logoFloat}>
                <Image
                  src={brand.logo || fullLogoBlack.src}
                  alt={`${getBrandDisplayName(brand, locale)} ${t('card.giftCards')}`}
                  width={280}
                  height={200}
                  loading="lazy"
                  sizes="(max-width: 640px) 50vw, 280px"
                  unoptimized
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = fullLogoBlack.src;
                  }}
                />
              </div>
            </div>

            <div className={styles.brandInfo}>
              <h2 className={styles.brandName}>
                {getBrandDisplayName(brand, locale)}
              </h2>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};
